const STOCK_PREVIEW_API = "https://pmsapi.infomanav.in/api/v1/stock/preview";

const parseDotNetDate = (value) => {
  if (!value) return null;
  const raw = String(value);
  const match = raw.match(/\/Date\((\d+)\)\//);
  if (!match) return null;
  const ms = Number(match[1]);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms);
};

const MONTH_INDEX = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

/** Parses AngelOne feed times like "15-Jul-2026 15:29:59" (IST). */
const parseAngelDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const raw = String(value).trim();
  const match = raw.match(
    /^(\d{1,2})-([A-Za-z]{3})-(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/,
  );
  if (match) {
    const day = Number(match[1]);
    const month = MONTH_INDEX[match[2]];
    const year = Number(match[3]);
    const hour = Number(match[4]);
    const minute = Number(match[5]);
    const second = Number(match[6]);
    if (month === undefined) return null;
    // Construct as IST wall-clock
    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}+05:30`;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const resolveAngelExchangeCode = (Exch, ExchSeg) => {
  const seg = String(ExchSeg || "").toUpperCase();
  const exch = String(Exch || "").toUpperCase();
  if (exch === "B" || exch.startsWith("BSE") || seg.startsWith("BSE")) return "BSE";
  if (exch === "M" || exch.includes("MCX") || seg.includes("MCX")) return "MCX";
  if (exch === "N" || exch.startsWith("NSE") || seg.startsWith("NSE") || !exch) {
    return "NSE";
  }
  if (exch.length > 1) return exch;
  return "NSE";
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const toBrokerFlag = (brokerType) => {
  const normalized = String(brokerType || "").trim().toLowerCase();
  if (normalized === "grow" || normalized === "5paisa" || normalized === "angelone") {
    return normalized;
  }
  return "5paisa";
};

class StockPreviewMarketSnapshotBase {
  constructor() {
    this.broker = "";
    this.scripCode = null;
    this.exchange = "";
    this.exchangeType = "";
    this.open = null;
    this.dayHigh = null;
    this.dayLow = null;
    this.previousClose = null;
    this.lastTradedPrice = null;
    this.netChange = null;
    this.volume = null;
    this.averageTradePrice = null;
    this.allTimeHigh = null;
    this.allTimeLow = null;
    this.upperCircuitLimit = null;
    this.lowerCircuitLimit = null;
    this.marketCap = null;
    this.totalBuyQuantity = null;
    this.totalSellQuantity = null;
    this.topBuyQuantity = null;
    this.topSellQuantity = null;
    this.lastQuantity = null;
    this.lastTradeTime = null;
    this.openInterest = null;
    this.exposureCategory = null;
    this.percentChange = null;
    this.tradingSymbol = "";
  }

  formatCurrency(value) {
    const num = toNumber(value);
    if (num === null) return "-";
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatInteger(value) {
    const num = toNumber(value);
    if (num === null) return "-";
    return num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
  }

  formatPercentFromChange(change, base) {
    const changeNum = toNumber(change);
    const baseNum = toNumber(base);
    if (changeNum === null || baseNum === null || baseNum === 0) return null;
    return ((changeNum / baseNum) * 100).toFixed(2);
  }

  formatLastTradeTime() {
    if (!this.lastTradeTime) return "-";
    return this.lastTradeTime.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  num(field) {
    return toNumber(this[field]);
  }

  getChangePercent() {
    if (this.percentChange !== null && this.percentChange !== undefined && this.percentChange !== "") {
      const pct = toNumber(this.percentChange);
      if (pct !== null) return pct.toFixed(2);
    }
    return this.formatPercentFromChange(this.netChange, this.previousClose);
  }

  getIsPositiveChange() {
    const change = toNumber(this.netChange);
    if (change === null) return null;
    return change >= 0;
  }

  getRangePosition(value, low, high) {
    const v = toNumber(value);
    const lo = toNumber(low);
    const hi = toNumber(high);
    if (v === null || lo === null || hi === null || hi === lo) return 50;
    return Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));
  }

  getBuySellPressure() {
    const buy = toNumber(this.totalBuyQuantity) || 0;
    const sell = toNumber(this.totalSellQuantity) || 0;
    const total = buy + sell;
    if (!total) {
      return { buy, sell, buyPct: 50, sellPct: 50, ratio: null };
    }
    return {
      buy,
      sell,
      buyPct: (buy / total) * 100,
      sellPct: (sell / total) * 100,
      ratio: sell > 0 ? buy / sell : null,
    };
  }

  getOhlcChartData() {
    return [
      { name: "Open", value: toNumber(this.open) || 0, fill: "#64748B" },
      { name: "High", value: toNumber(this.dayHigh) || 0, fill: "#16a34a" },
      { name: "Low", value: toNumber(this.dayLow) || 0, fill: "#dc2626" },
      { name: "Close", value: toNumber(this.previousClose) || 0, fill: "#2457BF" },
      {
        name: "LTP",
        value: toNumber(this.lastTradedPrice) || 0,
        fill: "#1A42A0",
      },
      {
        name: "ATP",
        value: toNumber(this.averageTradePrice) || 0,
        fill: "#7C8DB5",
      },
    ];
  }

  getBuySellChartData() {
    const pressure = this.getBuySellPressure();
    return [
      {
        name: "Buy",
        quantity: pressure.buy,
        topQty: toNumber(this.topBuyQuantity) || 0,
        fill: "#16a34a",
      },
      {
        name: "Sell",
        quantity: pressure.sell,
        topQty: toNumber(this.topSellQuantity) || 0,
        fill: "#dc2626",
      },
    ];
  }

  getDisplaySections() {
    const changePct = this.formatPercentFromChange(
      this.netChange,
      this.previousClose,
    );

    return [
      {
        title: "Session",
        items: [
          { label: "Open", value: this.formatCurrency(this.open) },
          { label: "High", value: this.formatCurrency(this.dayHigh) },
          { label: "Low", value: this.formatCurrency(this.dayLow) },
          { label: "Prev. Close", value: this.formatCurrency(this.previousClose) },
          { label: "LTP", value: this.formatCurrency(this.lastTradedPrice) },
          {
            label: "Net Change",
            value:
              this.netChange === null
                ? "-"
                : `${toNumber(this.netChange) >= 0 ? "+" : ""}${this.formatCurrency(this.netChange)}${changePct ? ` (${changePct}%)` : ""}`,
            tone:
              toNumber(this.netChange) === null
                ? "neutral"
                : toNumber(this.netChange) >= 0
                  ? "positive"
                  : "negative",
          },
          { label: "Volume", value: this.formatInteger(this.volume) },
          {
            label: "Avg. Trade Price",
            value: this.formatCurrency(this.averageTradePrice),
          },
        ],
      },
      {
        title: "Range & Circuits",
        items: [
          { label: "All Time High", value: this.formatCurrency(this.allTimeHigh) },
          { label: "All Time Low", value: this.formatCurrency(this.allTimeLow) },
          {
            label: "Upper Circuit",
            value: this.formatCurrency(this.upperCircuitLimit),
          },
          {
            label: "Lower Circuit",
            value: this.formatCurrency(this.lowerCircuitLimit),
          },
        ],
      },
      {
        title: "Market Stats",
        items: [
          {
            label: "Market Cap (Cr.)",
            value: this.formatCurrency(this.marketCap),
          },
          {
            label: "Total Buy Qty",
            value: this.formatInteger(this.totalBuyQuantity),
          },
          {
            label: "Total Sell Qty",
            value: this.formatInteger(this.totalSellQuantity),
          },
          {
            label: "Top Bid Qty",
            value: this.formatInteger(this.topBuyQuantity),
          },
          {
            label: "Top Ask Qty",
            value: this.formatInteger(this.topSellQuantity),
          },
          { label: "Open Interest", value: this.formatInteger(this.openInterest) },
        ],
      },
      {
        title: "Last Trade",
        items: [
          { label: "Last Qty", value: this.formatInteger(this.lastQuantity) },
          { label: "Last Trade Time", value: this.formatLastTradeTime() },
        ],
      },
    ];
  }
}

class FivePaisaStockPreviewMarketSnapshot extends StockPreviewMarketSnapshotBase {
  static fromApiPayload(marketSnapshotBlock = {}) {
    const raw = marketSnapshotBlock?.data || marketSnapshotBlock || {};
    const snapshot = new FivePaisaStockPreviewMarketSnapshot();

    snapshot.broker = "5paisa";
    snapshot.scripCode = toNumber(raw.ScripCode);
    snapshot.exchange = String(raw.Exchange || "");
    snapshot.exchangeType = String(raw.ExchangeType || "");
    snapshot.open = raw.Open;
    snapshot.dayHigh = raw.High;
    snapshot.dayLow = raw.Low;
    snapshot.previousClose = raw.PClose;
    snapshot.lastTradedPrice = raw.LastTradedPrice;
    snapshot.netChange = raw.NetChange;
    snapshot.volume = raw.Volume;
    snapshot.averageTradePrice = raw.AverageTradePrice;
    snapshot.allTimeHigh = raw.AHigh;
    snapshot.allTimeLow = raw.ALow;
    snapshot.upperCircuitLimit = raw.UpperCircuitLimit;
    snapshot.lowerCircuitLimit = raw.LowerCircuitLimit;
    snapshot.marketCap = raw.MarketCapital;
    snapshot.totalBuyQuantity = raw.TotalBuyQuantity;
    snapshot.totalSellQuantity = raw.TotalSellQuantity;
    snapshot.topBuyQuantity = raw.BuyQuantity;
    snapshot.topSellQuantity = raw.SellQuantity;
    snapshot.lastQuantity = raw.LastQuantity;
    snapshot.lastTradeTime = parseDotNetDate(raw.LastTradeTime);
    snapshot.openInterest = raw.OpenInterest;
    snapshot.exposureCategory = raw.ExposureCategory;

    return snapshot;
  }
}

class GrowwStockPreviewMarketSnapshot extends StockPreviewMarketSnapshotBase {
  static fromApiPayload() {
    return new GrowwStockPreviewMarketSnapshot();
  }
}

class AngelOneStockPreviewMarketSnapshot extends StockPreviewMarketSnapshotBase {
  static fromApiPayload(marketSnapshotBlock = {}) {
    const raw = marketSnapshotBlock?.data || marketSnapshotBlock || {};
    const snapshot = new AngelOneStockPreviewMarketSnapshot();

    snapshot.broker = "angelone";
    snapshot.scripCode = toNumber(raw.symbolToken);
    snapshot.exchange = String(raw.exchange || "");
    snapshot.exchangeType = "";
    snapshot.tradingSymbol = String(raw.tradingSymbol || "");
    snapshot.open = raw.open;
    snapshot.dayHigh = raw.high;
    snapshot.dayLow = raw.low;
    // AngelOne "close" is previous close in this feed
    snapshot.previousClose = raw.close;
    snapshot.lastTradedPrice = raw.ltp;
    snapshot.netChange = raw.netChange;
    snapshot.percentChange = raw.percentChange;
    snapshot.volume = raw.tradeVolume;
    snapshot.averageTradePrice = raw.avgPrice;
    snapshot.allTimeHigh = raw["52WeekHigh"];
    snapshot.allTimeLow = raw["52WeekLow"];
    snapshot.upperCircuitLimit = raw.upperCircuit;
    snapshot.lowerCircuitLimit = raw.lowerCircuit;
    snapshot.marketCap = null;
    snapshot.totalBuyQuantity = raw.totBuyQuan;
    snapshot.totalSellQuantity = raw.totSellQuan;
    snapshot.topBuyQuantity = null;
    snapshot.topSellQuantity = null;
    snapshot.lastQuantity = raw.lastTradeQty;
    snapshot.lastTradeTime = parseAngelDateTime(raw.exchTradeTime || raw.exchFeedTime);
    snapshot.openInterest = raw.opnInterest;
    snapshot.exposureCategory = null;

    return snapshot;
  }
}

class StockPreviewResponse {
  constructor({ broker, marketSnapshot, news, companyInfo, financials, optionChain }) {
    this.broker = broker;
    this.marketSnapshot = marketSnapshot;
    this.news = news;
    this.companyInfo = companyInfo;
    this.financials = financials;
    this.optionChain = optionChain;
  }
}

class BaseStockPreviewAdapter {
  getBrokerFlag() {
    throw new Error("getBrokerFlag() must be implemented");
  }

  supportsPreview() {
    return true;
  }

  buildRequestBody({ userId, Exch, ExchType, ExchSeg, ScripCode, symbolToken }) {
    throw new Error("buildRequestBody() must be implemented");
  }

  isValidRequestBody(body) {
    return Boolean(body && Object.keys(body).length);
  }

  parseResponse(json) {
    throw new Error("parseResponse() must be implemented");
  }
}

class FivePaisaStockPreviewAdapter extends BaseStockPreviewAdapter {
  getBrokerFlag() {
    return "5paisa";
  }

  buildRequestBody({ userId, Exch, ExchType, ScripCode }) {
    return {
      user_id: Number(userId) || 0,
      broker: this.getBrokerFlag(),
      Exch: String(Exch || "N").charAt(0).toUpperCase(),
      ExchType: String(ExchType || "C").charAt(0).toUpperCase(),
      ScripCode: Number(ScripCode) || 0,
    };
  }

  isValidRequestBody(body) {
    return Boolean(body?.ScripCode);
  }

  parseResponse(json) {
    const data = json?.data || {};
    return new StockPreviewResponse({
      broker: this.getBrokerFlag(),
      marketSnapshot: FivePaisaStockPreviewMarketSnapshot.fromApiPayload(
        data.market_snapshot,
      ),
      news: Array.isArray(data.news) ? data.news : [],
      companyInfo: Array.isArray(data.company_info) ? data.company_info : [],
      financials: Array.isArray(data.financials) ? data.financials : [],
      optionChain: Array.isArray(data.option_chain) ? data.option_chain : [],
    });
  }
}

class GrowwStockPreviewAdapter extends BaseStockPreviewAdapter {
  getBrokerFlag() {
    return "grow";
  }

  supportsPreview() {
    return false;
  }

  buildRequestBody() {
    return {};
  }

  parseResponse() {
    return new StockPreviewResponse({
      broker: this.getBrokerFlag(),
      marketSnapshot: GrowwStockPreviewMarketSnapshot.fromApiPayload(),
      news: [],
      companyInfo: [],
      financials: [],
      optionChain: [],
    });
  }
}

class AngelOneStockPreviewAdapter extends BaseStockPreviewAdapter {
  getBrokerFlag() {
    return "angelone";
  }

  supportsPreview() {
    return true;
  }

  buildRequestBody({ userId, Exch, ExchSeg, ScripCode, symbolToken }) {
    return {
      user_id: Number(userId) || 0,
      broker: this.getBrokerFlag(),
      exchange: resolveAngelExchangeCode(Exch, ExchSeg),
      symboltoken: String(symbolToken || ScripCode || "").trim(),
    };
  }

  isValidRequestBody(body) {
    return Boolean(body?.symboltoken);
  }

  parseResponse(json) {
    const data = json?.data || {};
    return new StockPreviewResponse({
      broker: this.getBrokerFlag(),
      marketSnapshot: AngelOneStockPreviewMarketSnapshot.fromApiPayload(
        data.market_snapshot,
      ),
      news: Array.isArray(data.news) ? data.news : [],
      companyInfo: Array.isArray(data.company_info) ? data.company_info : [],
      financials: Array.isArray(data.financials) ? data.financials : [],
      optionChain: Array.isArray(data.option_chain) ? data.option_chain : [],
    });
  }
}

class StockPreviewService {
  constructor() {
    this.apiUrl = STOCK_PREVIEW_API;
    this.adapters = {
      "5paisa": new FivePaisaStockPreviewAdapter(),
      grow: new GrowwStockPreviewAdapter(),
      angelone: new AngelOneStockPreviewAdapter(),
    };
  }

  getAdapter(brokerType) {
    const flag = toBrokerFlag(brokerType);
    return this.adapters[flag] || this.adapters["5paisa"];
  }

  getUserId() {
    try {
      const shared = JSON.parse(localStorage.getItem("sharedData") || "{}");
      return Number(shared?.ID || localStorage.getItem("id") || 0);
    } catch (_) {
      return 0;
    }
  }

  async fetchPreview(
    {
      broker,
      userId,
      Exch,
      ExchType,
      ExchSeg,
      ScripCode,
      symbolToken,
    },
    options = {},
  ) {
    const adapter = this.getAdapter(broker);
    if (!adapter.supportsPreview()) {
      throw new Error(`Stock preview is not supported for ${adapter.getBrokerFlag()}`);
    }

    const body = adapter.buildRequestBody({
      userId: userId ?? this.getUserId(),
      Exch,
      ExchType,
      ExchSeg,
      ScripCode,
      symbolToken,
    });

    if (!adapter.isValidRequestBody(body)) {
      throw new Error("Missing required stock preview params");
    }

    const response = await fetch(this.apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: options?.signal,
    });

    let json = null;
    try {
      json = await response.json();
    } catch (_) {
      throw new Error("Invalid JSON response from stock preview API");
    }

    const status = String(json?.status || "").toLowerCase();
    if (!response.ok || (status && status !== "success")) {
      throw new Error(json?.message || "Stock preview request failed");
    }

    return adapter.parseResponse(json);
  }
}

const stockPreviewService = new StockPreviewService();

export {
  StockPreviewMarketSnapshotBase,
  FivePaisaStockPreviewMarketSnapshot,
  GrowwStockPreviewMarketSnapshot,
  AngelOneStockPreviewMarketSnapshot,
  StockPreviewResponse,
  BaseStockPreviewAdapter,
  FivePaisaStockPreviewAdapter,
  GrowwStockPreviewAdapter,
  AngelOneStockPreviewAdapter,
  StockPreviewService,
  toBrokerFlag,
};

export default stockPreviewService;
